import {
  IDevice,
  IRoomSummary,
  IRoomValidationStatusCode,
  ITrendBucketConfig,
  ITrendKeyPath,
} from "@backend/src/schemas";
import { MCBimSpaceFillColors, PartialMcBimSpace } from "../../../utils";
import { ISpaceValidationRun } from "../../designSlice";
import {
  ROOM_VALIDATION_STATUS_CODES,
  ROOM_VALIDATION_STATUS_LABELS,
} from "../../../constantsFromBackend";

export interface IColorSpec {
  id: string;
  fillColor: MCBimSpaceFillColors | string;
  borderColor?: string;
  explanation: string;

  /**
   * Optional numeric bucketing metadata used for BOTH:
   * - polygon coloring (front-end)
   * - trend bucketing (sent to backend as ITrendBucketConfig)
   */
  range?: {
    /** value must be > minExclusive */
    minExclusive?: number;
    /** value must be >= minInclusive */
    minInclusive?: number;
    /** value must be <= maxInclusive */
    maxInclusive?: number;
  };
}

export interface IPolygonColors {
  fill?: MCBimSpaceFillColors | string;
  stroke?: string;
}

interface IGetColorArgs {
  room?: IRoomSummary;
  space?: PartialMcBimSpace;
  colorSpecs: IColorSpec[];
}

export enum COLOR_FILTERS_IDS {
  SPACE_BELONG_TO_STANDARD_ROOM = "SPACE_BELONG_TO_STANDARD_ROOM",
  SUPPLY_AIR_FLOW = "SUPPLY_AIR_FLOW",
  RETURN_AIR_FLOW = "RETURN_AIR_FLOW",
  MMI = "MMI",
  DR_STATUS = "DR_STATUS",
}

const BLUE_GRADIENTS: Record<string, string> = {
  VERY_LIGHT: "#E0FFFF",
  LIGHT: "#00FFFF",
  MEDIUM: "#0080FF",
  DARK: "#0000FF",
  VERY_DARK: "#00008B",
};

const YELLOW_GRADIENTS: Record<string, string> = {
  VERY_LIGHT: "#FFFFE0",
  LIGHT: "#FFFF00",
  MEDIUM: "#FFD700",
  DARK: "#FFA500",
  VERY_DARK: "#FF8C00",
};

export const MMI_COLORS: Record<number, string> = {
  0: "#D73296",
  100: "#BE2823",
  125: "#D24B46",
  150: "#E17873",
  175: "#F0AAAA",
  200: "#E69637",
  225: "#EBAF64",
  250: "#F0C88C",
  275: "#F5E6D7",
  300: "#FAF050",
  325: "#D7CD41",
  350: "#B9AF3C",
  375: "#9B9632",
  400: "#378246",
  425: "#4BAA5A",
  450: "#64C37D",
  475: "#9BD7A5",
  500: "#1F46AF",
  600: "#9B00CD",
} as const;

export const DR_STATUS_COLORS: Record<string, string> = {
  "": "#CED4DA",
  "–": "#CED4DA",
  dR0: "#7B0F14",
  dR1: "#E03131",
  dR2: "#F76707",
  dR3: "#FCC419",
  dR4: "#2F9E44",
} as const;

export const GRAY = "#d6d6d6";

/* -------------------------------------------------------------------------- */
/*  Range helpers (single source of truth for both map + trend)               */
/* -------------------------------------------------------------------------- */

const EPS = 1e-9;
const DEFAULT_OPEN_MAX = 1e12;

// Hjälp att hämta "ingen data"-färg från colorSpecs om du har den, annars fallback
const pickNoDataColor = (args: IGetColorArgs, fallbackGray: string): string => {
  const noData = args.colorSpecs?.find((s) => s.id === "NO_DATA")?.fillColor;
  return (noData as string) ?? fallbackGray;
};

const pickColorFromRangedSpecs = (
  value: number | null | undefined,
  specs: IColorSpec[],
  noDataColor: string,
  treatZeroAsNoData: boolean
): string => {
  if (value == null) return noDataColor;
  if (treatZeroAsNoData && value === 0) return noDataColor;

  for (const s of specs) {
    const r = s.range;
    if (!r) continue;

    if (r.minInclusive != null && value < r.minInclusive) continue;
    if (r.minExclusive != null && value <= r.minExclusive) continue;
    if (r.maxInclusive != null && value > r.maxInclusive) continue;

    return s.fillColor as string;
  }

  return noDataColor;
};

/**
 * Convert ranged color specs into backend bucket config.
 * Use this in the selector that builds the trend request payload.
 */
export const colorSpecsToTrendBucketConfig = (
  specs: IColorSpec[]
): ITrendBucketConfig | undefined => {
  const ranged = specs.filter((s) => s.range != null);
  if (ranged.length === 0) return undefined;

  return {
    kind: "ranges",
    ranges: ranged.map((s) => {
      const r = s.range!;
      const min =
        r.minInclusive != null ? r.minInclusive : r.minExclusive != null ? r.minExclusive + EPS : 0;

      const max = r.maxInclusive != null ? r.maxInclusive : DEFAULT_OPEN_MAX;

      return { id: s.id, min, max };
    }),
  };
};

/* -------------------------------------------------------------------------- */
/*  Bas-färgfilter (standardrom / tilluft / avtrekk)                          */
/* -------------------------------------------------------------------------- */

const roomHasStandardRoom = (args: IGetColorArgs): string | undefined => {
  if (args.room?.rfpMalRom != null && args.room.rfpMalRom !== "") {
    return args.colorSpecs.find((c) => c.id === "spaceBelongToStandardRoom")?.fillColor;
  } else {
    return args.colorSpecs.find((c) => c.id === "spaceNotBelongToStandardRoom")?.fillColor;
  }
};

export interface IColorFilterItem {
  id: COLOR_FILTERS_IDS | string;
  title: string;
  getPolygonColors: (args: IGetColorArgs) => IPolygonColors;
  colorSpecs: IColorSpec[];
  trendKeyPath?: ITrendKeyPath;
}

export const COLOR_FILTERS: IColorFilterItem[] = [
  {
    id: COLOR_FILTERS_IDS.SPACE_BELONG_TO_STANDARD_ROOM,
    title: "Standardrom",
    // trendKeyPath: undefined (not trended)
    colorSpecs: [
      {
        id: "spaceBelongToStandardRoom",
        fillColor: "#84f884",
        explanation: " Rommet er tilknyttet en standardromtype",
      },
      {
        id: "spaceNotBelongToStandardRoom",
        fillColor: "#ffffff",
        explanation: "Rommet er ikke tilknyttet en standardromtype",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => ({
      fill: roomHasStandardRoom(args),
    }),
  },
  {
    id: COLOR_FILTERS_IDS.SUPPLY_AIR_FLOW,
    title: "Tilluft",
    trendKeyPath: "supplyAirFlow",
    colorSpecs: [
      {
        id: "VERY_LOW",
        fillColor: BLUE_GRADIENTS.VERY_LIGHT,
        explanation: "1-50 m3/h",
        range: { minInclusive: 1, maxInclusive: 50 },
      },
      {
        id: "LOW",
        fillColor: BLUE_GRADIENTS.LIGHT,
        explanation: "51-100 m3/h",
        range: { minInclusive: 51, maxInclusive: 100 },
      },
      {
        id: "MEDIUM",
        fillColor: BLUE_GRADIENTS.MEDIUM,
        explanation: "101-200 m3/h",
        range: { minInclusive: 101, maxInclusive: 200 },
      },
      {
        id: "HIGH",
        fillColor: BLUE_GRADIENTS.DARK,
        explanation: "201-300 m3/h",
        range: { minInclusive: 201, maxInclusive: 300 },
      },
      {
        id: "VERY_HIGH",
        fillColor: BLUE_GRADIENTS.VERY_DARK,
        explanation: ">300 m3/h",
        range: { minExclusive: 300 },
      },
      {
        id: "NO_DATA",
        fillColor: GRAY,
        explanation: "Ingen data",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const v = args.room?.supplyAirFlow;
      const noData = pickNoDataColor(args, GRAY);
      // Keep behavior consistent with "NO_DATA": treat 0 as no data
      return { fill: pickColorFromRangedSpecs(v, args.colorSpecs, noData, true) };
    },
  },
  {
    id: COLOR_FILTERS_IDS.RETURN_AIR_FLOW,
    title: "Avtrekk",
    trendKeyPath: "returnAirFlow",
    colorSpecs: [
      {
        id: "VERY_LOW",
        fillColor: YELLOW_GRADIENTS.VERY_LIGHT,
        explanation: "1-50 m3/h",
        range: { minInclusive: 1, maxInclusive: 50 },
      },
      {
        id: "LOW",
        fillColor: YELLOW_GRADIENTS.LIGHT,
        explanation: "51-100 m3/h",
        range: { minInclusive: 51, maxInclusive: 100 },
      },
      {
        id: "MEDIUM",
        fillColor: YELLOW_GRADIENTS.MEDIUM,
        explanation: "101-200 m3/h",
        range: { minInclusive: 101, maxInclusive: 200 },
      },
      {
        id: "HIGH",
        fillColor: YELLOW_GRADIENTS.DARK,
        explanation: "201-300 m3/h",
        range: { minInclusive: 201, maxInclusive: 300 },
      },
      {
        id: "VERY_HIGH",
        fillColor: YELLOW_GRADIENTS.VERY_DARK,
        explanation: ">300 m3/h",
        range: { minExclusive: 300 },
      },
      {
        id: "NO_DATA",
        fillColor: GRAY,
        explanation: "0 eller Ingen data",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const v = args.room?.returnAirFlow;
      const noData = pickNoDataColor(args, GRAY);
      return { fill: pickColorFromRangedSpecs(v, args.colorSpecs, noData, true) };
    },
  },
  {
    id: COLOR_FILTERS_IDS.MMI,
    title: "MMI",
    trendKeyPath: "extraIfcInfo.mmi",
    colorSpecs: [
      ...Object.entries(MMI_COLORS)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([code, hex]) => ({
          id: code,
          fillColor: hex,
          explanation: `MMI ${code}`,
        })),
      {
        id: "NO_DATA",
        fillColor: GRAY,
        explanation: "Ingen data / ukjent MMI",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const raw = (args.room as any)?.extraIfcInfo?.mmi;

      const code =
        typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : undefined;

      const fill =
        typeof code === "number" && Number.isFinite(code) && MMI_COLORS[code]
          ? MMI_COLORS[code]
          : pickNoDataColor(args, GRAY);

      return { fill };
    },
  },
  {
    id: COLOR_FILTERS_IDS.DR_STATUS,
    title: "dR-status",
    trendKeyPath: "drStatus",
    colorSpecs: [
      { id: "dR0", fillColor: DR_STATUS_COLORS.dR0, explanation: "dR0" },
      { id: "dR1", fillColor: DR_STATUS_COLORS.dR1, explanation: "dR1" },
      { id: "dR2", fillColor: DR_STATUS_COLORS.dR2, explanation: "dR2" },
      { id: "dR3", fillColor: DR_STATUS_COLORS.dR3, explanation: "dR3" },
      { id: "dR4", fillColor: DR_STATUS_COLORS.dR4, explanation: "dR4" },
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen status" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const key = typeof args.room?.drStatus === "string" ? args.room.drStatus.trim() : "";
      const fill = DR_STATUS_COLORS[key] ?? pickNoDataColor(args, GRAY);
      return { fill };
    },
  },
];

/* -------------------------------------------------------------------------- */
/*  Standardrom-validering: färgskala + stroke för manuellt godkänd          */
/* -------------------------------------------------------------------------- */

export type IValidationDecisionColorSpec =
  | {
      id: IRoomValidationStatusCode;
      label: string;
      fillColor: string;
    }
  | {
      id: "NO_DATA";
      label: string;
      fillColor: string;
    };

export const ROOM_VALIDATION_STATUS_COLORS: Record<IRoomValidationStatusCode, string> = {
  APPROVED: "#1B7F3A",
  APPROVED_WITH_DEVIATIONS: "#FFA94D",
  NEEDS_CHECK: "#FF3B30",
};

export const VALIDATION_DECISION_COLOR_SPECS: IColorSpec[] = [
  ...ROOM_VALIDATION_STATUS_CODES.map((code) => ({
    id: code,
    fillColor: ROOM_VALIDATION_STATUS_COLORS[code],
    explanation: ROOM_VALIDATION_STATUS_LABELS[code],
  })),
  {
    id: "NO_DATA",
    fillColor: GRAY,
    explanation: "Ingen status",
  },
];

export const PERCENT_COLORS = Object.freeze({
  ZERO: "#FF3B30",
  P01_20: "#FF6B6B",
  P21_40: "#FFA94D",
  P41_60: "#FFD43B",
  P61_80: "#82C91E",
  P81_99: "#1B7F3A",
  FULL: "#00E676",
});

export const getPercentColor = (value: number): string => {
  const p = Math.max(0, Math.min(100, Math.floor(value)));
  if (p === 100) return PERCENT_COLORS.FULL;
  if (p >= 81) return PERCENT_COLORS.P81_99;
  if (p >= 61) return PERCENT_COLORS.P61_80;
  if (p >= 41) return PERCENT_COLORS.P41_60;
  if (p >= 21) return PERCENT_COLORS.P21_40;
  if (p >= 1) return PERCENT_COLORS.P01_20;
  return PERCENT_COLORS.ZERO;
};

export const STANDARD_VALIDATION_MANUAL_SPEC_ID = "MANUAL_APPROVED";

export const STANDARD_VALIDATION_COLOR_SPECS: IColorSpec[] = [
  {
    id: "ZERO",
    fillColor: PERCENT_COLORS.ZERO,
    explanation: "0 %",
    range: { minInclusive: 0, maxInclusive: 0 },
  },
  {
    id: "P01_20",
    fillColor: PERCENT_COLORS.P01_20,
    explanation: "1–20 %",
    range: { minInclusive: 1, maxInclusive: 20 },
  },
  {
    id: "P21_40",
    fillColor: PERCENT_COLORS.P21_40,
    explanation: "21–40 %",
    range: { minInclusive: 21, maxInclusive: 40 },
  },
  {
    id: "P41_60",
    fillColor: PERCENT_COLORS.P41_60,
    explanation: "41–60 %",
    range: { minInclusive: 41, maxInclusive: 60 },
  },
  {
    id: "P61_80",
    fillColor: PERCENT_COLORS.P61_80,
    explanation: "61–80 %",
    range: { minInclusive: 61, maxInclusive: 80 },
  },
  {
    id: "P81_99",
    fillColor: PERCENT_COLORS.P81_99,
    explanation: "81–99 %",
    range: { minInclusive: 81, maxInclusive: 99 },
  },
  {
    id: "FULL",
    fillColor: PERCENT_COLORS.FULL,
    explanation: "100 %",
    range: { minInclusive: 100, maxInclusive: 100 },
  },
  { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen data" },
  {
    id: STANDARD_VALIDATION_MANUAL_SPEC_ID,
    fillColor: "#ffffff",
    borderColor: "#0070F3",
    explanation: "Manuelt godkjent (blå kant)",
  },
];

// Convenience export for backend payload when trending standard validation percent
export const STANDARD_VALIDATION_PERCENT_BUCKET_CONFIG: ITrendBucketConfig | undefined =
  colorSpecsToTrendBucketConfig(STANDARD_VALIDATION_COLOR_SPECS);

/**
 * Factory: bygg en getPolygonColors-funktion for f.eks. "standardRoom.E"
 * (fill is percent, stroke can be used for manual approval if you want later)
 */
export const makeStandardRoomValidationColorFn = (key: `standardRoom.${string}`) => {
  const discipline = key.split(".")[1];

  return (args: IGetColorArgs): IPolygonColors => {
    const std = args.room?.validation?.standardRoom as
      | Record<string, { summary?: { matchRatePct?: number } }>
      | undefined;

    const approvals = args.room?.validationDecisions?.standardRoom as
      | Record<string, { approved?: boolean }>
      | undefined;

    const pct = std?.[discipline]?.summary?.matchRatePct;
    const isApproved = approvals?.[discipline]?.approved === true;

    const fill =
      pct == null || Number.isNaN(pct) ? pickNoDataColor(args, GRAY) : getPercentColor(pct);

    // If you later want stroke for manual approval, you can use isApproved here.
    void isApproved;

    return { fill };
  };
};

export const makeValidationDecisionColorFn = (decisionKey: `${string}.${string}`) => {
  const [branch, discipline] = decisionKey.split(".") as [string, string];

  return (args: IGetColorArgs): IPolygonColors => {
    const decisions = args.room?.validationDecisions;
    if (!decisions) {
      return { fill: pickNoDataColor(args, GRAY), stroke: undefined };
    }

    const record = (decisions as Record<string, unknown>)[branch] as
      | Record<string, { current?: { status?: IRoomValidationStatusCode } }>
      | undefined;

    const status = record?.[discipline]?.current?.status;

    if (!status) {
      return { fill: pickNoDataColor(args, GRAY), stroke: undefined };
    }

    const fill = ROOM_VALIDATION_STATUS_COLORS[status] ?? pickNoDataColor(args, GRAY);
    return { fill, stroke: undefined };
  };
};

/* -------------------------------------------------------------------------- */
/*  Viewer-integration                                                        */
/* -------------------------------------------------------------------------- */

export interface IArgGetColor {
  theSpaceNumber: string;
  activeSpace: {
    spaceNumber: string;
    spaceLongName: string;
  } | null;
  mcbimSpacesNameAndLongName: { spaceNumber: string; spaceLongName: string }[];
  selectedSpaces: string[];
  spaceValid: boolean | undefined;
  copySpaceModeActive: boolean;
  devicesExistsInSpace: boolean;
  spaceValidationRun: ISpaceValidationRun;
  activeColorPanelItem: COLOR_FILTERS_IDS | string | undefined;
  room?: IRoomSummary | undefined;
  space?: PartialMcBimSpace | undefined;
  freeTextSearchHit: boolean;
  deviceOnTheMove?: IDevice;
  nonPublischedDevicesFromAlgosSpaceNumbers: string[];
  colorFilters: IColorFilterItem[];
}

export const getPolygonColorsOfPixPolygonSpace = (args: IArgGetColor): IPolygonColors => {
  // 1) Device on the move – behåll gamla beteendet (ingen override)
  if (args.deviceOnTheMove?.roomNumber === args.theSpaceNumber) {
    return {};
  }

  // 2) Fritextsök – blå fill
  if (args.freeTextSearchHit) {
    return { fill: MCBimSpaceFillColors.BLUE };
  }

  // 3) Rum med opubliserade devices från algoritmer – gul fill
  if (args.nonPublischedDevicesFromAlgosSpaceNumbers.includes(args.theSpaceNumber)) {
    return { fill: MCBimSpaceFillColors.YELLOW };
  }

  // 4) Copy-space-läge
  if (args.copySpaceModeActive) {
    const mcbimSpace = args.mcbimSpacesNameAndLongName.find(
      (space) => space.spaceNumber === args.theSpaceNumber
    );
    if (mcbimSpace != null) {
      const longName = mcbimSpace.spaceLongName;
      if (args.activeSpace?.spaceLongName === longName) {
        return { fill: MCBimSpaceFillColors.GREY };
      }
    }
  }

  // 5) Färgfilter-panel (COLORING-tabben)
  if (args.activeColorPanelItem != null) {
    const colorFilter = args.colorFilters.find((filter) => filter.id === args.activeColorPanelItem);

    if (!colorFilter) return {};

    const colors = colorFilter.getPolygonColors({
      room: args.room as IRoomSummary,
      space: args.space,
      colorSpecs: colorFilter.colorSpecs,
    });

    return colors;
  }

  // 6) Space validation run – röd/grön fill
  if (args.spaceValidationRun.active) {
    const spaceValidationResult = args.spaceValidationRun.spacesValidationsResults.find(
      (result) => result.spaceNumber === args.theSpaceNumber
    );
    if (spaceValidationResult != null) {
      const allValid = spaceValidationResult.spaceValidations.every(
        (validation) => validation.status === "VALID"
      );
      return {
        fill: allValid ? MCBimSpaceFillColors.GREEN : MCBimSpaceFillColors.RED,
      };
    }
    return {};
  }

  // 7) Aktivt rum + devices + valid/invalid
  if (args.activeSpace?.spaceNumber === args.theSpaceNumber && args.devicesExistsInSpace) {
    if (args.spaceValid) {
      return { fill: MCBimSpaceFillColors.GREEN };
    }
    if (!args.spaceValid) {
      return { fill: MCBimSpaceFillColors.RED };
    }
  }

  // 8) Selected space (single) + devices
  if (
    args.theSpaceNumber === args.activeSpace?.spaceNumber &&
    args.theSpaceNumber === args.selectedSpaces[0] &&
    args.devicesExistsInSpace
  ) {
    if (args.spaceValid) {
      return { fill: MCBimSpaceFillColors.GREEN };
    }
    if (!args.spaceValid) {
      return { fill: MCBimSpaceFillColors.RED };
    }
  }

  // 9) Multi-selection – ljusbrun fill
  if (args.selectedSpaces.includes(args.theSpaceNumber ?? "")) {
    return { fill: MCBimSpaceFillColors.LIGHT_BROWN };
  }

  // 10) Default – inga overrides
  return {};
};

export const getFillColorOfPixPolygonSpace = (
  args: IArgGetColor
): MCBimSpaceFillColors | undefined => {
  const { fill } = getPolygonColorsOfPixPolygonSpace(args);
  return fill as MCBimSpaceFillColors | undefined;
};
